import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Field, Input, Select, Empty, Badge } from "@/components/ui-kit";
import { KeyRound, Copy, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = { parent: "Parent", player: "Joueur", coach: "Coach" };

export default function AccessCodes({ registrationOpen, onToggleRegistration }) {
  const [codes, setCodes] = useState([]);
  const [members, setMembers] = useState([]);
  const [gen, setGen] = useState({ role: "parent", member_id: "", email: "" });
  const [busy, setBusy] = useState(false);

  const loadCodes = () => api.get("/access-codes").then((r) => setCodes(r.data)).catch(() => {});
  useEffect(() => {
    loadCodes();
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
  }, []);

  const copyCode = async (code) => {
    try {
      if (!navigator.clipboard) throw new Error("no clipboard");
      await navigator.clipboard.writeText(code);
      toast.success(`Code copié : ${code}`);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = code; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        const ok = document.execCommand("copy"); document.body.removeChild(ta);
        if (ok) { toast.success(`Code copié : ${code}`); return; }
        throw new Error("failed");
      } catch { window.prompt("Copiez le code (Ctrl/Cmd + C) :", code); }
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/access-codes", {
        role: gen.role,
        member_id: gen.member_id || null,
        email: gen.email || null,
      });
      if (data.email_sent) toast.success(`Code ${data.code} généré et envoyé par email`);
      else toast.success(`Code généré : ${data.code}`);
      setGen({ role: "parent", member_id: "", email: "" });
      loadCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la génération");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/access-codes/${id}`);
    toast.success("Code supprimé");
    loadCodes();
  };

  return (
    <>
      <h2 className="font-display text-xl font-bold uppercase tracking-tight mt-10 mb-4 flex items-center gap-2">
        <KeyRound size={20} className="text-primary" /> Accès aux inscriptions
      </h2>

      <Card className="flex items-center justify-between mb-4">
        <div>
          <p className="font-semibold">Inscriptions libres</p>
          <p className="text-xs text-muted-foreground mt-1">
            Si désactivé, un code d'accès fourni par le club est obligatoire pour s'inscrire.
          </p>
        </div>
        <button data-testid="toggle-registration" onClick={onToggleRegistration}
          className={`relative w-14 h-8 rounded-full transition-colors ${registrationOpen ? "bg-primary" : "bg-secondary"}`}>
          <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-transform ${registrationOpen ? "translate-x-7" : "translate-x-1"}`} />
        </button>
      </Card>

      <Card className="mb-4">
        <p className="font-semibold flex items-center gap-2 mb-3"><UserPlus size={16} className="text-primary" /> Générer un code d'accès</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Rôle">
            <Select data-testid="code-role-select" value={gen.role} onChange={(e) => setGen({ ...gen, role: e.target.value })}>
              <option value="parent">Parent</option>
              <option value="player">Joueur</option>
              <option value="coach">Coach</option>
            </Select>
          </Field>
          <Field label="Lier à un membre (optionnel)">
            <Select data-testid="code-member-select" value={gen.member_id} onChange={(e) => setGen({ ...gen, member_id: e.target.value })}>
              <option value="">Aucun</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
            </Select>
          </Field>
          <Field label="Envoyer par email (optionnel)">
            <Input data-testid="code-email-input" type="email" placeholder="parent@exemple.com"
              value={gen.email} onChange={(e) => setGen({ ...gen, email: e.target.value })} />
          </Field>
        </div>
        <Button data-testid="generate-code-btn" onClick={generate} disabled={busy} className="mt-1">
          <KeyRound size={16} /> {busy ? "Génération..." : "Générer le code"}
        </Button>
      </Card>

      {codes.length ? (
        <div className="space-y-2">
          {codes.map((c) => (
            <Card key={c.id} data-testid="access-code-row" className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <span className="font-mono text-lg font-bold tracking-widest text-primary">{c.code}</span>
                <Badge color={c.used ? "red" : "primary"}>{c.used ? "Utilisé" : "Disponible"}</Badge>
                <Badge color="blue">{ROLE_LABELS[c.role] || c.role}</Badge>
                {c.member_name && <span className="text-sm text-muted-foreground">{c.member_name}</span>}
              </div>
              <div className="flex items-center gap-1">
                {!c.used && (
                  <button data-testid="copy-code-btn" onClick={() => copyCode(c.code)}
                    className="p-2 text-muted-foreground hover:text-primary" title="Copier"><Copy size={16} /></button>
                )}
                <button data-testid="delete-code-btn" onClick={() => remove(c.id)}
                  className="p-2 text-muted-foreground hover:text-destructive" title="Supprimer"><Trash2 size={16} /></button>
              </div>
            </Card>
          ))}
        </div>
      ) : <Empty text="Aucun code d'accès généré." />}
    </>
  );
}
